import React, { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

// Stessa formula usata in Layout.js per decidere se i testi della sidebar
// devono essere chiari o scuri in base al colore di sfondo scelto.
function isLightColor(hex) {
  if (!hex) return false;
  const c = hex.replace('#', '');
  if (c.length !== 6) return false;
  const r = parseInt(c.substring(0, 2), 16) / 255;
  const g = parseInt(c.substring(2, 4), 16) / 255;
  const b = parseInt(c.substring(4, 6), 16) / 255;
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 0.55;
}

export default function SettingsPage() {
  const { theme, updateTheme } = useTheme();
  const { user }               = useAuth();
  const [saved, setSaved]      = useState(false);

  const [form, setForm] = useState({
    companyName:    theme.companyName,
    primaryColor:   theme.primaryColor,
    secondaryColor: theme.secondaryColor,
    sidebarColor:   theme.sidebarColor,
    logoUrl:        theme.logoUrl || '',
  });

  // MFA
  const [mfaStep, setMfaStep]   = useState('idle'); // idle | loading | show | verify | done
  const [mfaData, setMfaData]   = useState(null);
  const [mfaCode, setMfaCode]   = useState('');
  const [mfaError, setMfaError] = useState('');

  // Password
  const [pwForm, setPwForm]   = useState({ old: '', new1: '', new2: '' });
  const [pwMsg, setPwMsg]     = useState('');
  const [pwError, setPwError] = useState('');

  // Gestione utenti (solo admin)
  const [users, setUsers]         = useState([]);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [newUser, setNewUser]     = useState({ username: '', email: '', password: '', full_name: '', role: 'viewer', allowed_customers: [] });
  const [userMsg, setUserMsg]     = useState('');
  const [userError, setUserError] = useState('');
  const [allCustomers, setAllCustomers] = useState([]);
  const [editingUserId, setEditingUserId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  // Gestione agenti (solo admin)
  const [agents, setAgents]           = useState([]);
  const [agentsLoaded, setAgentsLoaded] = useState(false);
  const [newAgentCustomer, setNewAgentCustomer] = useState('');
  const [newAgentName, setNewAgentName] = useState('');
  const [generatedToken, setGeneratedToken] = useState(null);
  const [agentMsg, setAgentMsg]       = useState('');
  const [agentError, setAgentError]   = useState('');
  const [confirmRevokeId, setConfirmRevokeId] = useState(null);

  // Gestione clienti (solo admin)
  const [newCustomerCode, setNewCustomerCode] = useState('');
  const [newCustomerName, setNewCustomerName] = useState('');
  const [customerMsg, setCustomerMsg]         = useState('');
  const [customerError, setCustomerError]     = useState('');
  const [confirmDeleteCustomerCode, setConfirmDeleteCustomerCode] = useState(null);

  // Gestione credenziali cliente (solo admin)
  const [credCustomer, setCredCustomer] = useState('');
  const [credType, setCredType]         = useState('pbs');
  const [credValues, setCredValues]     = useState({});
  const [existingCredKeys, setExistingCredKeys] = useState([]);
  const [credMsg, setCredMsg]           = useState('');
  const [credError, setCredError]       = useState('');

  const isAdmin = user?.role === 'admin';
  // La personalizzazione del branding è riservata esclusivamente
  // all'account "admin" originale, non a qualunque utente con ruolo admin.
  const isPrimaryAdmin = user?.username === 'admin';

  const loadUsers = async () => {
    try {
      const res = await api.get('/auth/users');
      setUsers(res.data);
      setUsersLoaded(true);
    } catch (err) {
      setUserError(err.response?.data?.detail || 'Errore caricamento utenti');
    }
  };

  const loadAllCustomers = async () => {
    try {
      const res = await api.get('/api/customers');
      setAllCustomers(res.data);
    } catch (err) {
      // Non bloccante: se fallisce, il multi-select resterà vuoto
    }
  };

  const handleCreateCustomer = async () => {
    setCustomerError(''); setCustomerMsg('');
    const code = newCustomerCode.trim().toUpperCase();
    const name = newCustomerName.trim().toUpperCase();
    if (!code || !name) {
      setCustomerError('Codice cliente e nome sono obbligatori'); return;
    }
    try {
      await api.post('/api/customers', { customer_code: code, customer_name: name });
      setCustomerMsg(`Cliente "${name}" (${code}) creato! Ora visibile nel selettore cliente e pronto per ricevere dati.`);
      setNewCustomerCode(''); setNewCustomerName('');
      loadAllCustomers();
    } catch (err) {
      setCustomerError(err.response?.data?.detail || 'Errore creazione cliente');
    }
  };

  const handleDeleteCustomer = async (code) => {
    setCustomerError(''); setCustomerMsg('');
    try {
      await api.delete(`/api/customers/${code}`);
      setCustomerMsg(`Cliente "${code}" eliminato.`);
      setConfirmDeleteCustomerCode(null);
      loadAllCustomers();
    } catch (err) {
      setCustomerError(err.response?.data?.detail || 'Errore eliminazione cliente');
    }
  };

  const loadAgents = async () => {
    try {
      const res = await api.get('/agent/admin/list');
      setAgents(res.data);
      setAgentsLoaded(true);
    } catch (err) {
      setAgentError(err.response?.data?.detail || 'Errore caricamento agenti');
    }
  };

  React.useEffect(() => {
    if (isAdmin && !usersLoaded) { loadUsers(); loadAllCustomers(); }
    if (isAdmin && !agentsLoaded) { loadAgents(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const resetUserForm = () => {
    setNewUser({ username: '', email: '', password: '', full_name: '', role: 'viewer', allowed_customers: [] });
    setEditingUserId(null);
  };

  const handleCreateUser = async () => {
    setUserError(''); setUserMsg('');
    if (!editingUserId && (!newUser.email || !newUser.password)) {
      setUserError('Email e password sono obbligatorie'); return;
    }
    if (newUser.password && newUser.password.length < 8) {
      setUserError('La password deve avere almeno 8 caratteri'); return;
    }
    try {
      if (editingUserId) {
        const payload = { ...newUser };
        if (!payload.password) delete payload.password; // password opzionale in modifica
        const res = await api.put(`/auth/users/${editingUserId}`, payload);
        setUserMsg(
          payload.password
            ? `Utente "${newUser.email}" aggiornato!${res.data.email_sent ? ' Nuova password inviata via email.' : ''}`
            : `Utente "${newUser.email}" aggiornato!`
        );
      } else {
        const res = await api.post('/auth/users', newUser);
        setUserMsg(
          `Utente "${newUser.email}" creato! ${res.data.email_sent ? 'Credenziali inviate via email.' : 'Invio email non riuscito: comunica le credenziali manualmente.'}`
        );
      }
      resetUserForm();
      loadUsers();
    } catch (err) {
      setUserError(err.response?.data?.detail || 'Errore salvataggio utente');
    }
  };

  const handleEditUser = (u) => {
    setEditingUserId(u.id);
    setNewUser({
      username: u.username,
      email: u.email || '',
      password: '',
      full_name: u.full_name || '',
      role: u.role,
      allowed_customers: u.allowed_customers || [],
    });
    setUserMsg(''); setUserError('');
  };

  const handleDeleteUser = async (u) => {
    setUserError(''); setUserMsg('');
    try {
      await api.delete(`/auth/users/${u.id}`);
      setUserMsg(`Utente "${u.username}" eliminato.`);
      setConfirmDeleteId(null);
      if (editingUserId === u.id) resetUserForm();
      loadUsers();
    } catch (err) {
      setUserError(err.response?.data?.detail || 'Errore eliminazione utente');
    }
  };

  const toggleAllowedCustomer = (code) => {
    setNewUser(f => {
      const has = f.allowed_customers.includes(code);
      return {
        ...f,
        allowed_customers: has
          ? f.allowed_customers.filter(c => c !== code)
          : [...f.allowed_customers, code],
      };
    });
  };

  const handleGenerateAgentToken = async () => {
    setAgentError(''); setAgentMsg(''); setGeneratedToken(null);
    if (!newAgentCustomer) {
      setAgentError('Seleziona il cliente per cui generare il token'); return;
    }
    try {
      const res = await api.post('/agent/admin/registration-token', {
        customer_code: newAgentCustomer,
        display_name: newAgentName,
      });
      setGeneratedToken(res.data);
      setAgentMsg(`Token generato per ${newAgentCustomer}, valido ${res.data.expires_in_minutes} minuti (uso singolo).`);
    } catch (err) {
      setAgentError(err.response?.data?.detail || 'Errore generazione token');
    }
  };

  const handleRevokeAgent = async (agent) => {
    setAgentError(''); setAgentMsg('');
    try {
      await api.delete(`/agent/admin/${agent.id}`);
      setAgentMsg(`Agent "${agent.display_name || agent.agent_id}" revocato.`);
      setConfirmRevokeId(null);
      loadAgents();
    } catch (err) {
      setAgentError(err.response?.data?.detail || 'Errore revoca agent');
    }
  };

  const handleCopyToken = () => {
    if (generatedToken?.registration_token) {
      navigator.clipboard.writeText(generatedToken.registration_token);
      setAgentMsg('Token copiato negli appunti!');
    }
  };

  // Campi richiesti per ciascuna tecnologia: si estende facilmente in
  // futuro (es. "veeam") aggiungendo una nuova voce qui, senza toccare il
  // resto della logica del form.
  const CRED_FIELDS = {
    pbs: [
      { key: 'host', label: 'Host PBS', placeholder: 'es. 192.168.1.246' },
      { key: 'port', label: 'Porta', placeholder: '8007' },
      { key: 'api_token_id', label: 'API Token ID', placeholder: 'root@pam!noc-token' },
      { key: 'api_token_secret', label: 'API Token Secret', placeholder: '', sensitive: true },
    ],
  };

  const loadExistingCredKeys = async (customerCode) => {
    if (!customerCode) { setExistingCredKeys([]); return; }
    try {
      const res = await api.get(`/agent/admin/credentials/${customerCode}`);
      setExistingCredKeys(res.data.map(r => `${r.credential_type}:${r.credential_key}`));
    } catch (err) {
      setExistingCredKeys([]);
    }
  };

  const handleCredCustomerChange = (code) => {
    setCredCustomer(code);
    setCredValues({});
    setCredMsg(''); setCredError('');
    loadExistingCredKeys(code);
  };

  const handleSaveCredentials = async () => {
    setCredError(''); setCredMsg('');
    if (!credCustomer) { setCredError('Seleziona un cliente'); return; }

    const fields = CRED_FIELDS[credType] || [];
    const toSave = fields.filter(f => credValues[f.key]);
    if (toSave.length === 0) {
      setCredError('Inserisci almeno un valore da salvare'); return;
    }

    try {
      for (const field of toSave) {
        await api.put('/agent/admin/credentials', {
          customer_code: credCustomer,
          credential_type: credType,
          credential_key: field.key,
          value: credValues[field.key],
        });
      }
      setCredMsg(`Credenziali ${credType.toUpperCase()} salvate per ${credCustomer}.`);
      setCredValues({});
      loadExistingCredKeys(credCustomer);
    } catch (err) {
      setCredError(err.response?.data?.detail || 'Errore salvataggio credenziali');
    }
  };

  const handleSave = async () => {
    try {
      await updateTheme({
        companyName:    form.companyName,
        primaryColor:   form.primaryColor,
        secondaryColor: form.secondaryColor,
        sidebarColor:   form.sidebarColor,
        logoUrl:        form.logoUrl || null,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setUserError(err.response?.data?.detail || 'Errore salvataggio impostazioni');
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setForm(f => ({ ...f, logoUrl: ev.target.result }));
    reader.readAsDataURL(file);
  };

  const handleMFASetup = async () => {
    setMfaStep('loading');
    try {
      const res = await api.get('/auth/mfa/setup');
      setMfaData(res.data);
      setMfaStep('show');
    } catch { setMfaStep('idle'); }
  };

  const handleMFAEnable = async () => {
    setMfaError('');
    try {
      await api.post('/auth/mfa/enable', { username: user.username, code: mfaCode });
      setMfaStep('done');
    } catch (err) {
      setMfaError(err.response?.data?.detail || 'Codice non valido');
    }
  };

  const handleChangePassword = async () => {
    setPwError(''); setPwMsg('');
    if (pwForm.new1 !== pwForm.new2) { setPwError('Le password non coincidono'); return; }
    if (pwForm.new1.length < 8)      { setPwError('Minimo 8 caratteri'); return; }
    try {
      await api.post('/auth/change-password', { old_password: pwForm.old, new_password: pwForm.new1 });
      setPwMsg('Password aggiornata!');
      setPwForm({ old: '', new1: '', new2: '' });
    } catch (err) {
      setPwError(err.response?.data?.detail || 'Errore aggiornamento password');
    }
  };

  return (
    <div>
      <h2 style={{ margin: '0 0 20px', fontSize: 22, fontWeight: 700, color: '#1e293b' }}>Impostazioni</h2>

      {/* ── Personalizzazione (solo admin) ── */}
      {isAdmin && (
      <div style={s.card}>
        <div style={s.cardTitle}>🎨 Personalizzazione</div>
        <div style={s.body}>
          <Row label="Nome azienda">
            <input style={s.input} value={form.companyName}
              onChange={e => setForm(f => ({ ...f, companyName: e.target.value }))} />
          </Row>
          <Row label="Colore primario">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input type="color" value={form.primaryColor}
                onChange={e => setForm(f => ({ ...f, primaryColor: e.target.value }))}
                style={{ width: 44, height: 36, border: 'none', cursor: 'pointer', borderRadius: 6 }} />
              <input style={{ ...s.input, width: 110 }} value={form.primaryColor}
                onChange={e => setForm(f => ({ ...f, primaryColor: e.target.value }))} />
            </div>
          </Row>
          <Row label="Colore secondario">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input type="color" value={form.secondaryColor}
                onChange={e => setForm(f => ({ ...f, secondaryColor: e.target.value }))}
                style={{ width: 44, height: 36, border: 'none', cursor: 'pointer', borderRadius: 6 }} />
              <input style={{ ...s.input, width: 110 }} value={form.secondaryColor}
                onChange={e => setForm(f => ({ ...f, secondaryColor: e.target.value }))} />
            </div>
          </Row>
          <Row label="Colore sidebar">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input type="color" value={form.sidebarColor}
                onChange={e => setForm(f => ({ ...f, sidebarColor: e.target.value }))}
                style={{ width: 44, height: 36, border: 'none', cursor: 'pointer', borderRadius: 6 }} />
              <input style={{ ...s.input, width: 110 }} value={form.sidebarColor}
                onChange={e => setForm(f => ({ ...f, sidebarColor: e.target.value }))} />
              <span style={{ fontSize: 12, color: '#64748b' }}>
                Testi e bordi si adattano automaticamente al colore scelto
              </span>
            </div>
          </Row>
          <Row label="Logo aziendale">
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              {form.logoUrl && (
                <img src={form.logoUrl} alt="logo preview"
                  style={{ height: 48, borderRadius: 8, border: '1px solid #e2e8f0' }} />
              )}
              <label style={{ ...s.btnSecondary, cursor: 'pointer' }}>
                📁 Carica immagine
                <input type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: 'none' }} />
              </label>
              {form.logoUrl && (
                <button style={s.btnDanger} onClick={() => setForm(f => ({ ...f, logoUrl: '' }))}>Rimuovi</button>
              )}
            </div>
          </Row>

          {/* Anteprima */}
          <div style={s.preview}>
            <div style={s.previewLabel}>Anteprima sidebar</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: form.sidebarColor, padding: '12px 16px', borderRadius: 10 }}>
              {form.logoUrl
                ? <img src={form.logoUrl} alt="logo" style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 8 }} />
                : <div style={{ width: 36, height: 36, borderRadius: 8, background: form.primaryColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 18, color: '#fff' }}>
                    {form.companyName?.charAt(0)}
                  </div>
              }
              <span style={{ color: isLightColor(form.sidebarColor) ? '#1e293b' : '#fff', fontWeight: 700 }}>{form.companyName}</span>
            </div>
          </div>

          <button onClick={handleSave} style={{ ...s.btnPrimary, background: form.primaryColor }}>
            {saved ? '✅ Salvato!' : '💾 Salva impostazioni'}
          </button>
        </div>
      </div>
      )}

      {/* ── MFA ── */}
      <div style={s.card}>
        <div style={s.cardTitle}>🔐 Autenticazione a due fattori (MFA)</div>
        <div style={s.body}>
          {mfaStep === 'idle' && (
            <>
              <p style={{ color: '#64748b', fontSize: 14, marginBottom: 16 }}>
                Attiva il MFA per proteggere il tuo account con Google Authenticator o Authy.
              </p>
              <button onClick={handleMFASetup} style={{ ...s.btnPrimary, background: '#7c3aed' }}>
                🔑 Configura MFA
              </button>
            </>
          )}

          {mfaStep === 'loading' && <p style={{ color: '#64748b' }}>Generazione QR code...</p>}

          {mfaStep === 'show' && mfaData && (
            <div>
              <p style={{ fontSize: 14, color: '#374151', marginBottom: 16 }}>
                Scansiona il QR code con la tua app Authenticator, poi inserisci il codice a 6 cifre per attivare.
              </p>
              <img src={mfaData.qr_code} alt="QR MFA" style={{ width: 200, height: 200, borderRadius: 12, border: '1px solid #e2e8f0', marginBottom: 16 }} />
              <div style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#64748b', marginBottom: 16, fontFamily: 'monospace' }}>
                Secret manuale: {mfaData.secret}
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <input
                  style={{ ...s.input, width: 140, letterSpacing: '0.2em', textAlign: 'center', fontSize: 18 }}
                  type="text" inputMode="numeric" maxLength={6}
                  placeholder="000000"
                  value={mfaCode}
                  onChange={e => setMfaCode(e.target.value.replace(/\D/g, ''))}
                />
                <button
                  onClick={handleMFAEnable}
                  disabled={mfaCode.length !== 6}
                  style={{ ...s.btnPrimary, background: '#7c3aed' }}
                >
                  Attiva MFA
                </button>
              </div>
              {mfaError && <div style={{ color: '#dc2626', fontSize: 13, marginTop: 8 }}>{mfaError}</div>}
            </div>
          )}

          {mfaStep === 'done' && (
            <div style={{ color: '#16a34a', fontWeight: 600, fontSize: 15 }}>
              ✅ MFA attivato con successo! Al prossimo login ti verrà chiesto il codice.
            </div>
          )}
        </div>
      </div>

      {/* ── Cambio password ── */}
      <div style={s.card}>
        <div style={s.cardTitle}>🔒 Cambio password</div>
        <div style={s.body}>
          <Row label="Password attuale">
            <input style={s.input} type="password" value={pwForm.old}
              onChange={e => setPwForm(f => ({ ...f, old: e.target.value }))} />
          </Row>
          <Row label="Nuova password">
            <input style={s.input} type="password" value={pwForm.new1}
              onChange={e => setPwForm(f => ({ ...f, new1: e.target.value }))} />
          </Row>
          <Row label="Conferma password">
            <input style={s.input} type="password" value={pwForm.new2}
              onChange={e => setPwForm(f => ({ ...f, new2: e.target.value }))} />
          </Row>
          {pwError && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 8 }}>{pwError}</div>}
          {pwMsg   && <div style={{ color: '#16a34a', fontSize: 13, marginBottom: 8 }}>{pwMsg}</div>}
          <button onClick={handleChangePassword} style={{ ...s.btnPrimary, background: '#1e293b' }}>
            Aggiorna password
          </button>
        </div>
      </div>

      {/* ── Gestione clienti (solo admin) ── */}
      {isAdmin && (
        <div style={s.card}>
          <div style={s.cardTitle}>🏢 Gestione clienti</div>
          <div style={s.body}>
            <p style={{ color: '#64748b', fontSize: 13, marginBottom: 20 }}>
              Crea un nuovo cliente prima di configurare l'agent corrispondente: comparirà subito
              nel selettore cliente e sarà pronto per ricevere dati, anche prima del primo import.
            </p>

            <div style={{ marginBottom: 24 }}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Codice</th>
                    <th style={s.th}>Nome</th>
                    <th style={s.th}></th>
                  </tr>
                </thead>
                <tbody>
                  {allCustomers.length === 0 && (
                    <tr><td style={s.td} colSpan={3}>Nessun cliente presente.</td></tr>
                  )}
                  {allCustomers.map(c => (
                    <tr key={c.customer_code}>
                      <td style={s.td}>{c.customer_code}</td>
                      <td style={s.td}>{c.customer_name}</td>
                      <td style={s.td}>
                        {confirmDeleteCustomerCode === c.customer_code ? (
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={() => handleDeleteCustomer(c.customer_code)} style={s.btnDanger}>Confermi?</button>
                            <button onClick={() => setConfirmDeleteCustomerCode(null)} style={s.btnSecondary}>Annulla</button>
                          </div>
                        ) : (
                          <button onClick={() => setConfirmDeleteCustomerCode(c.customer_code)} style={s.btnDanger}>Elimina</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 20 }}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16, color: '#1e293b' }}>
                ➕ Nuovo cliente
              </div>
              <Row label="Codice cliente">
                <input style={s.input} value={newCustomerCode}
                  placeholder="es. CL010"
                  onChange={e => setNewCustomerCode(e.target.value)} />
              </Row>
              <Row label="Nome cliente">
                <input style={s.input} value={newCustomerName}
                  placeholder="es. NUOVA AZIENDA SRL"
                  onChange={e => setNewCustomerName(e.target.value)} />
              </Row>
              {customerError && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 8 }}>{customerError}</div>}
              {customerMsg   && <div style={{ color: '#16a34a', fontSize: 13, marginBottom: 8 }}>{customerMsg}</div>}
              <button onClick={handleCreateCustomer} style={{ ...s.btnPrimary, background: '#1e293b' }}>
                Crea cliente
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Gestione agenti (solo admin) ── */}
      {isAdmin && (
        <div style={s.card}>
          <div style={s.cardTitle}>🤖 Gestione agenti</div>
          <div style={s.body}>
            <p style={{ color: '#64748b', fontSize: 13, marginBottom: 20 }}>
              Gli agent sostituiscono la API Key statica condivisa con un'identità dedicata per
              ciascun cliente, revocabile singolarmente senza impattare gli altri. Genera un token
              di registrazione temporaneo (valido 60 minuti, uso singolo) per configurare un nuovo
              agent su un server cliente.
            </p>

            <div style={{ marginBottom: 24 }}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Cliente</th>
                    <th style={s.th}>Nome agent</th>
                    <th style={s.th}>Stato</th>
                    <th style={s.th}>Creato</th>
                    <th style={s.th}>Ultima comunicazione</th>
                    <th style={s.th}></th>
                  </tr>
                </thead>
                <tbody>
                  {agents.length === 0 && (
                    <tr><td style={s.td} colSpan={6}>Nessun agent registrato.</td></tr>
                  )}
                  {agents.map(a => (
                    <tr key={a.id}>
                      <td style={s.td}>{a.customer_code}</td>
                      <td style={s.td}>{a.display_name || '—'}</td>
                      <td style={s.td}>
                        <span style={{
                          ...s.badge,
                          background: a.is_active ? '#dcfce7' : '#fef2f2',
                          color: a.is_active ? '#16a34a' : '#dc2626',
                        }}>
                          {a.is_active ? 'Attivo' : 'Revocato'}
                        </span>
                      </td>
                      <td style={s.td}>{a.created_at ? new Date(a.created_at).toLocaleString('it-IT') : '—'}</td>
                      <td style={s.td}>{a.last_seen_at ? new Date(a.last_seen_at).toLocaleString('it-IT') : 'Mai'}</td>
                      <td style={s.td}>
                        {a.is_active && (
                          confirmRevokeId === a.id ? (
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button onClick={() => handleRevokeAgent(a)} style={s.btnDanger}>Confermi?</button>
                              <button onClick={() => setConfirmRevokeId(null)} style={s.btnSecondary}>Annulla</button>
                            </div>
                          ) : (
                            <button onClick={() => setConfirmRevokeId(a.id)} style={s.btnDanger}>Revoca</button>
                          )
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 20 }}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16, color: '#1e293b' }}>
                ➕ Genera token di registrazione
              </div>
              <Row label="Cliente">
                <select style={s.input} value={newAgentCustomer}
                  onChange={e => setNewAgentCustomer(e.target.value)}>
                  <option value="">Seleziona cliente...</option>
                  {allCustomers.map(c => (
                    <option key={c.customer_code} value={c.customer_code}>{c.customer_name}</option>
                  ))}
                </select>
              </Row>
              <Row label="Nome agent (opzionale)">
                <input style={s.input} value={newAgentName}
                  placeholder="es. Veeam-PEZZANA, PBS-Teamwork"
                  onChange={e => setNewAgentName(e.target.value)} />
              </Row>
              {agentError && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 8 }}>{agentError}</div>}
              {agentMsg   && <div style={{ color: '#16a34a', fontSize: 13, marginBottom: 8 }}>{agentMsg}</div>}
              <button onClick={handleGenerateAgentToken} style={{ ...s.btnPrimary, background: '#1e293b' }}>
                Genera token
              </button>

              {generatedToken && (
                <div style={{ marginTop: 16, background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8, padding: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#92400e', marginBottom: 8 }}>
                    ⚠️ Copia ora questo token — non sarà più visibile dopo aver lasciato la pagina
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <code style={{ flex: 1, background: '#fff', padding: '8px 12px', borderRadius: 6, fontSize: 13, wordBreak: 'break-all' }}>
                      {generatedToken.registration_token}
                    </code>
                    <button onClick={handleCopyToken} style={s.btnSecondary}>📋 Copia</button>
                  </div>
                  <div style={{ fontSize: 12, color: '#92400e', marginTop: 8 }}>
                    Valido {generatedToken.expires_in_minutes} minuti, utilizzabile una sola volta.
                    Sul server cliente esegui: <code>python3 register_agent.py</code>
                  </div>
                </div>
              )}
            </div>

            <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 20, marginTop: 24 }}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8, color: '#1e293b' }}>
                🔑 Credenziali cliente
              </div>
              <p style={{ color: '#64748b', fontSize: 13, marginBottom: 16 }}>
                Le credenziali verso il sistema sorgente (PBS, ecc.) sono cifrate e custodite qui
                sul NOC: l'agent le recupera al volo ad ogni esecuzione, non vengono mai salvate
                sul server cliente.
              </p>
              <Row label="Cliente">
                <select style={s.input} value={credCustomer}
                  onChange={e => handleCredCustomerChange(e.target.value)}>
                  <option value="">Seleziona cliente...</option>
                  {allCustomers.map(c => (
                    <option key={c.customer_code} value={c.customer_code}>{c.customer_name}</option>
                  ))}
                </select>
              </Row>
              <Row label="Tecnologia">
                <select style={s.input} value={credType}
                  onChange={e => { setCredType(e.target.value); setCredValues({}); }}>
                  <option value="pbs">Proxmox Backup Server</option>
                </select>
              </Row>

              {credCustomer && (CRED_FIELDS[credType] || []).map(field => {
                const alreadySet = existingCredKeys.includes(`${credType}:${field.key}`);
                return (
                  <Row key={field.key} label={field.label}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <input
                        style={s.input}
                        type={field.sensitive ? 'password' : 'text'}
                        placeholder={alreadySet ? '••••••••  (già configurato, lascia vuoto per non modificare)' : field.placeholder}
                        value={credValues[field.key] || ''}
                        onChange={e => setCredValues(v => ({ ...v, [field.key]: e.target.value }))}
                      />
                      {alreadySet && (
                        <span style={{ fontSize: 12, color: '#16a34a', fontWeight: 600 }}>✓ configurato</span>
                      )}
                    </div>
                  </Row>
                );
              })}

              {credCustomer && (
                <>
                  {credError && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 8 }}>{credError}</div>}
                  {credMsg   && <div style={{ color: '#16a34a', fontSize: 13, marginBottom: 8 }}>{credMsg}</div>}
                  <button onClick={handleSaveCredentials} style={{ ...s.btnPrimary, background: '#1e293b' }}>
                    Salva credenziali
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {isAdmin && (
        <div style={s.card}>
          <div style={s.cardTitle}>👥 Gestione utenti</div>
          <div style={s.body}>
            <div style={{ marginBottom: 24 }}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Email (Username)</th>
                    <th style={s.th}>Nome</th>
                    <th style={s.th}>Ruolo</th>
                    <th style={s.th}>Clienti accessibili</th>
                    <th style={s.th}>MFA</th>
                    <th style={s.th}>Attivo</th>
                    <th style={s.th}>Ultimo accesso</th>
                    <th style={s.th}></th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id}>
                      <td style={s.td}>{u.email || u.username}</td>
                      <td style={s.td}>{u.full_name || '—'}</td>
                      <td style={s.td}>
                        <span style={{
                          ...s.badge,
                          background: u.role === 'admin' ? '#ede9fe' : '#f1f5f9',
                          color: u.role === 'admin' ? '#7c3aed' : '#475569',
                        }}>
                          {u.role}
                        </span>
                      </td>
                      <td style={s.td}>
                        {u.role === 'admin'
                          ? <span style={{ color: '#64748b', fontStyle: 'italic' }}>Tutti</span>
                          : (u.allowed_customers && u.allowed_customers.length > 0
                              ? u.allowed_customers.join(', ')
                              : <span style={{ color: '#64748b', fontStyle: 'italic' }}>Tutti (nessuna restrizione)</span>)}
                      </td>
                      <td style={s.td}>{u.mfa_enabled ? '✅' : '—'}</td>
                      <td style={s.td}>{u.is_active ? '✅' : '❌'}</td>
                      <td style={s.td}>
                        {u.last_login ? new Date(u.last_login).toLocaleString('it-IT') : 'Mai'}
                      </td>
                      <td style={s.td}>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button onClick={() => handleEditUser(u)} style={s.btnSecondary}>Modifica</button>
                          {confirmDeleteId === u.id ? (
                            <>
                              <button onClick={() => handleDeleteUser(u)} style={s.btnDanger}>Confermi?</button>
                              <button onClick={() => setConfirmDeleteId(null)} style={s.btnSecondary}>Annulla</button>
                            </>
                          ) : (
                            <button onClick={() => setConfirmDeleteId(u.id)} style={s.btnDanger}>Elimina</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 20 }}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16, color: '#1e293b' }}>
                {editingUserId ? `✏️ Modifica utente "${newUser.email}"` : '➕ Crea nuovo utente'}
              </div>
              <Row label="Email (sarà l'username)">
                <input style={s.input} type="email" value={newUser.email} disabled={!!editingUserId}
                  onChange={e => setNewUser(f => ({ ...f, email: e.target.value }))} />
              </Row>
              <Row label="Nome completo">
                <input style={s.input} value={newUser.full_name}
                  onChange={e => setNewUser(f => ({ ...f, full_name: e.target.value }))} />
              </Row>
              <Row label={editingUserId ? 'Nuova password (opzionale)' : 'Password'}>
                <input style={s.input} type="password" value={newUser.password}
                  placeholder={editingUserId ? 'Lascia vuoto per non modificarla' : ''}
                  onChange={e => setNewUser(f => ({ ...f, password: e.target.value }))} />
              </Row>
              <Row label="Ruolo">
                <select style={s.input} value={newUser.role}
                  onChange={e => setNewUser(f => ({ ...f, role: e.target.value }))}>
                  <option value="viewer">Viewer (solo lettura)</option>
                  <option value="admin">Admin (accesso completo)</option>
                </select>
              </Row>
              {newUser.role !== 'admin' && (
                <Row label="Clienti accessibili">
                  <div>
                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>
                      Nessuna selezione = accesso a tutti i clienti
                    </div>
                    <div style={{
                      display: 'flex', flexWrap: 'wrap', gap: 8, maxWidth: 480,
                      border: '1.5px solid #d1d5db', borderRadius: 8, padding: 12,
                    }}>
                      {allCustomers.length === 0 && (
                        <span style={{ fontSize: 13, color: '#94a3b8' }}>Nessun cliente disponibile</span>
                      )}
                      {allCustomers.map(c => {
                        const checked = newUser.allowed_customers.includes(c.customer_code);
                        return (
                          <label key={c.customer_code} style={{
                            display: 'flex', alignItems: 'center', gap: 6, fontSize: 13,
                            padding: '4px 10px', borderRadius: 6, cursor: 'pointer',
                            background: checked ? '#ede9fe' : '#f8fafc',
                            color: checked ? '#7c3aed' : '#475569',
                          }}>
                            <input type="checkbox" checked={checked}
                              onChange={() => toggleAllowedCustomer(c.customer_code)} />
                            {c.customer_name}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </Row>
              )}
              {newUser.role === 'admin' && (
                <Row label="Clienti accessibili">
                  <div style={{ fontSize: 13, color: '#64748b', fontStyle: 'italic' }}>
                    Gli account admin vedono sempre tutti i clienti
                  </div>
                </Row>
              )}
              {userError && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 8 }}>{userError}</div>}
              {userMsg   && <div style={{ color: '#16a34a', fontSize: 13, marginBottom: 8 }}>{userMsg}</div>}
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={handleCreateUser} style={{ ...s.btnPrimary, background: '#1e293b' }}>
                  {editingUserId ? 'Salva modifiche' : 'Crea utente'}
                </button>
                {editingUserId && (
                  <button onClick={resetUserForm} style={s.btnSecondary}>Annulla modifica</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 20 }}>
      <div style={{ width: 160, fontSize: 14, fontWeight: 600, color: '#374151', flexShrink: 0 }}>{label}</div>
      <div>{children}</div>
    </div>
  );
}

const s = {
  card:        { background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', marginBottom: 20 },
  cardTitle:   { padding: '14px 20px', fontWeight: 700, fontSize: 15, color: '#1e293b', borderBottom: '1px solid #f1f5f9' },
  body:        { padding: '20px 24px' },
  input:       { padding: '8px 12px', borderRadius: 8, border: '1.5px solid #d1d5db', fontSize: 14, outline: 'none' },
  btnPrimary:  { padding: '10px 24px', borderRadius: 8, border: 'none', color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer' },
  btnSecondary:{ padding: '8px 16px', borderRadius: 8, border: '1.5px solid #d1d5db', background: '#f8fafc', color: '#374151', fontWeight: 600, fontSize: 13 },
  btnDanger:   { padding: '8px 16px', borderRadius: 8, border: 'none', background: '#fef2f2', color: '#dc2626', fontWeight: 600, fontSize: 13, cursor: 'pointer' },
  preview:     { background: '#f8fafc', borderRadius: 10, padding: 16, marginBottom: 20 },
  previewLabel:{ fontSize: 12, color: '#64748b', marginBottom: 10, fontWeight: 600 },
  table:       { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th:          { textAlign: 'left', padding: '8px 12px', borderBottom: '2px solid #e2e8f0', color: '#64748b', fontWeight: 700, fontSize: 12 },
  td:          { padding: '10px 12px', borderBottom: '1px solid #f1f5f9', color: '#374151' },
  badge:       { padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700 },
};
